/**
 * Rating-drop monitor — per (outlet, source) current vs prev-7d / prev-28d.
 * Reads marketing.outlet_rating_trend view; severe drops surface here AND
 * feed Active Fires via the same view.
 */
import { useMemo } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Globe,
  Minus,
  ShoppingBag,
  Trophy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useOutlets } from '@/hooks/useOutlets';
import {
  type RatingSeverity,
  type RatingSource,
  type RatingTrendRow,
  useRatingTrends,
} from '@/hooks/useRatingTrends';
import { cn } from '@/lib/utils';

const SOURCES: RatingSource[] = ['google', 'swiggy', 'zomato'];

const SOURCE_LABEL: Record<RatingSource, string> = {
  google: 'Google',
  swiggy: 'Swiggy',
  zomato: 'Zomato',
};

const SOURCE_ICON: Record<RatingSource, typeof Globe> = {
  google: Globe,
  swiggy: ShoppingBag,
  zomato: Trophy,
};

export function RatingTrends() {
  const outlets = useOutlets();
  const trends = useRatingTrends();

  // Index rows by (outlet, source) for O(1) lookup in the grid.
  const grid = useMemo(() => {
    const map = new Map<string, RatingTrendRow>();
    for (const r of trends.data ?? []) map.set(`${r.outlet_id}::${r.source}`, r);
    return map;
  }, [trends.data]);

  const severeFires = useMemo(
    () =>
      (trends.data ?? [])
        .filter((r) => r.severity === 'severe')
        .sort((a, b) => (Number(a.delta_28d) || 0) - (Number(b.delta_28d) || 0)),
    [trends.data],
  );

  // Order outlets so the ones with severe drops bubble to the top.
  const orderedOutlets = useMemo(() => {
    const list = outlets.data ?? [];
    const severity = (oid: string) => {
      const sev = SOURCES.map((s) => grid.get(`${oid}::${s}`)?.severity);
      if (sev.includes('severe')) return 0;
      if (sev.includes('mild')) return 1;
      return 2;
    };
    return [...list].sort((a, b) => severity(a.id) - severity(b.id) || a.name.localeCompare(b.name));
  }, [outlets.data, grid]);

  const loading = outlets.isLoading || trends.isLoading;

  return (
    <div className="space-y-4">
      {/* Severe-drop fires */}
      {severeFires.length > 0 && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4" />
              Rating drops ({severeFires.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-1.5 text-sm">
              {severeFires.slice(0, 8).map((f) => {
                const outlet = outlets.data?.find((o) => o.id === f.outlet_id);
                return (
                  <li key={`${f.outlet_id}-${f.source}`} className="flex items-start gap-2">
                    <span className="text-rose-600 dark:text-rose-400">●</span>
                    <span className="flex-1">
                      <strong>{outlet?.name ?? f.outlet_id}</strong>
                      <span className="ml-1 text-muted-foreground">on</span>
                      <span className="ml-1 capitalize">{SOURCE_LABEL[f.source]}</span>
                      <span className="ml-2 text-muted-foreground">
                        now {Number(f.current_rating).toFixed(2)}★
                      </span>
                      {f.delta_28d != null && (
                        <span className="ml-2 font-semibold text-rose-600 dark:text-rose-400">
                          {Number(f.delta_28d) > 0 ? '+' : ''}
                          {Number(f.delta_28d).toFixed(2)} (28d)
                        </span>
                      )}
                      {f.review_count != null && f.review_count < 10 && (
                        <span className="ml-2 text-2xs text-muted-foreground">
                          · only {f.review_count} reviews — low sample
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ratings per outlet · 28-day trend</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : orderedOutlets.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No outlets yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-2xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-left">Outlet</th>
                    {SOURCES.map((s) => {
                      const Icon = SOURCE_ICON[s];
                      return (
                        <th key={s} className="px-3 py-2 text-left">
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="h-3 w-3" /> {SOURCE_LABEL[s]}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {orderedOutlets.map((o) => (
                    <tr key={o.id} className="border-b border-border/30 last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{o.name}</div>
                        {o.city && (
                          <div className="text-2xs text-muted-foreground">{o.city}</div>
                        )}
                      </td>
                      {SOURCES.map((s) => {
                        const row = grid.get(`${o.id}::${s}`);
                        return (
                          <td key={s} className="px-3 py-2.5">
                            <RatingCell row={row} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RatingCell({ row }: { row: RatingTrendRow | undefined }) {
  if (!row || row.current_rating == null) {
    return <span className="text-2xs text-muted-foreground">no data</span>;
  }
  const cur = Number(row.current_rating);
  const d28 = row.delta_28d != null ? Number(row.delta_28d) : null;
  const d7 = row.delta_7d != null ? Number(row.delta_7d) : null;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5">
        <span className={cn('font-semibold tabular-nums', ratingColor(cur))}>
          {cur.toFixed(2)}★
        </span>
        <SeverityBadge severity={row.severity} />
      </div>
      <div className="flex items-center gap-2 text-2xs text-muted-foreground">
        <Delta value={d7} label="7d" />
        <Delta value={d28} label="28d" />
        {row.review_count != null && row.review_count > 0 && (
          <span className="tabular-nums">{row.review_count} rvw</span>
        )}
      </div>
    </div>
  );
}

function Delta({ value, label }: { value: number | null; label: string }) {
  if (value == null) {
    return <span className="text-muted-foreground">{label}: —</span>;
  }
  const arrow =
    value > 0.01 ? (
      <ArrowUp className="h-2.5 w-2.5" />
    ) : value < -0.01 ? (
      <ArrowDown className="h-2.5 w-2.5" />
    ) : (
      <Minus className="h-2.5 w-2.5" />
    );
  const color =
    value > 0.01
      ? 'text-emerald-600 dark:text-emerald-400'
      : value < -0.01
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-muted-foreground';
  return (
    <span className={cn('inline-flex items-center gap-0.5 tabular-nums', color)}>
      {arrow}
      {value > 0 ? '+' : ''}
      {value.toFixed(2)} {label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: RatingSeverity }) {
  if (severity === 'stable') return null;
  const map: Record<RatingSeverity, { label: string; cls: string } | null> = {
    severe: {
      label: 'severe',
      cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
    },
    mild: {
      label: 'drop',
      cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    },
    improving: {
      label: '↑',
      cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    },
    stable: null,
    unknown: null,
  };
  const v = map[severity];
  if (!v) return null;
  return (
    <Badge variant="muted" className={cn('text-2xs', v.cls)}>
      {v.label}
    </Badge>
  );
}

function ratingColor(r: number): string {
  if (r >= 4.3) return 'text-emerald-700 dark:text-emerald-400';
  if (r >= 4.0) return 'text-amber-700 dark:text-amber-400';
  return 'text-rose-700 dark:text-rose-400';
}
